import { Dispatch, useCallback } from 'react';
import {
  registerEditorAndGetSnapshot,
  setValue,
} from '@decisions/server/DecisionsFramework/Design/Properties/ContextProviderService';
import { ContextProviderNode } from '@decisions/server/DecisionsFramework/Design/Properties/ContextProviderNode';
import { ContextChangeAction } from '../contextProviderNodeReducer';
import { IAddBeforeSavePromises } from 'hooks/useBeforeSavePromises';
import { createDataPairValue } from 'utils/DataPairUtils';
import { DataPair } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ContextData';
import { PROVIDER_NODE_PATH_SEPARATOR } from '../utils/ProviderNodeUtils';

export interface IUseUpdatesPropertyEditors extends IAddBeforeSavePromises {
  /** needs no introduction */
  editingContext: string;
  /** immediately update node(s) */
  dispatchNodeChange: Dispatch<ContextChangeAction>;
  /** debounce batching node change events */
  debouncedUpdate: Dispatch<ContextChangeAction> & { clear(): void } & { flush(): void };
  setIsNodesLoading?: (isLoading: boolean) => void;
  beforeSnapshotUpdate?: (snapshot: ContextProviderNode) => ContextProviderNode;
}

export function useUpdatesPropertyEditors({
  addBeforeSavePromise,
  dispatchNodeChange,
  debouncedUpdate,
  editingContext,
  removeBeforeSavePromise,
  setIsNodesLoading,
  beforeSnapshotUpdate,
}: IUseUpdatesPropertyEditors) {
  const setSnapShot = (value: ContextProviderNode) => {
    // clear any debounced updates from events, just in case:
    debouncedUpdate.clear();
    dispatchNodeChange({ value });
  };

  const refresh = async () => {
    if (!editingContext) {
      return;
    }

    // Using registerEditorAndGetSnapshot to ensure editor is registered for client events, etc.
    // The register part of this is ignored on the server if it's already registered.
    setIsNodesLoading && setIsNodesLoading(true);
    let snap = await registerEditorAndGetSnapshot({
      contextProviderRegistrationId: editingContext,
    });
    if (beforeSnapshotUpdate) {
      snap = beforeSnapshotUpdate(snap);
    }
    setIsNodesLoading && setIsNodesLoading(false);
    setSnapShot(snap);

    // we have a useEffect hook to handle updating validations on snapshot changes, now,
    // so we don't need to do so here, or it will happen twice.
  };

  const addEditPromise = useCallback(
    // if when we have un-mounting triggering editComplete, we'll need to track & await these here, too
    (id: string, promise: Promise<any>) => {
      addBeforeSavePromise?.(id, promise);
    },
    [addBeforeSavePromise]
  );

  const removeEditPromise = useCallback(
    (id: string) => {
      removeBeforeSavePromise?.(id);
    },
    [removeBeforeSavePromise]
  );

  const onChange = useCallback(
    async (val: any, path: string[]) => {
      const pathString = path.join(PROVIDER_NODE_PATH_SEPARATOR);
      const setValuePromise = new Promise<boolean>((res, rej) => {
        const value: DataPair = createDataPairValue(val);
        // assign the value
        setValue({ contextProviderRegistrationId: editingContext, value, path })
          .then(() => {
            res(true);
            removeEditPromise(pathString);
          })
          .catch(err => {
            rej();
            refresh();
            removeEditPromise(pathString);
            throw err;
          });
      });

      // add set value promise to make we call onchange before edit complete
      addEditPromise(pathString, setValuePromise);

      // await for setvalue, otherwise edit complete will not wait for set value
      await setValuePromise;
    },
    // we need a new onChange only if the editing context changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingContext]
  );

  return { addEditPromise, removeEditPromise, refresh, onChange, setSnapShot };
}
